/**
 * services/workflow.ts · 业务联动 workflow 服务
 *
 * 替代 v200.16.1 散乱在 IIFE 里的业务联动逻辑:
 *   - 报价确认 → 自动转销售订单 + 创建工单
 *   - PR 审批 → 自动转 PO + 通知供应商
 *   - WO 完工 → 自动入库 + 触发 AR 开票预备
 *   - 收款 → 自动 UPDATE 发票 paid_amount + 触发 AR 余额刷新
 *
 * 设计原则:
 *   - 服务层薄包装 SQL · 不耦合 UI
 *   - 每个 workflow 独立 · 可独立测试
 *   - 失败 ROLLBACK 不残留半成品状态
 */

import { getDb, saveDb, query } from '@core/db';
import { sendNotification } from './notify';

export interface WorkflowResult {
  ok: boolean;
  message: string;
  newRecord?: { type: string; id: number; code: string };
}

/**
 * 转报价为销售订单 · 销售流核心
 * 1. 查 quotation 详情
 * 2. INSERT sales_orders (复用客户 + 金额)
 * 3. UPDATE quotation.status = 'confirmed'
 * 4. saveDb
 */
export async function convertQuotationToSO(quotationId: number): Promise<WorkflowResult> {
  try {
    // v358.2 P0 #6 · 拉 urgency + notes (老 schema 列缺时各列单独查)
    const q = query<{ id: number; code: string; customer_id: number; total: number; status: string }>(
      `SELECT id, code, customer_id, total, status FROM quotations WHERE id = ?`, [quotationId]
    )[0];
    if (!q) return { ok: false, message: '❌ 报价不存在' };
    if (q.status === 'confirmed') return { ok: false, message: '⚠️ 该报价已转 SO' };
    // urgency 单独查 (列可能不存在)
    const urgRow = query<{ urgency: string | null; notes: string | null }>(`SELECT urgency, notes FROM quotations WHERE id = ?`, [quotationId])[0];
    const qUrgency = (urgRow?.urgency || (urgRow?.notes || '').includes('🔥') ? 'urgent' : 'normal');

    // v358.1 · 拉报价行项 (套盒多组件支持 · 单行兼容兜底)
    interface QLine {
      line_no: number; product_id: number | null; product_description: string | null;
      box_type: string | null; dim_mm: string | null; material: string | null;
      gsm_spec: string | null; area_m2: number | null; qty: number; unit_price: number; line_total: number | null;
    }
    let qLines: QLine[] = [];
    try {
      qLines = query<QLine>(`SELECT line_no, product_id, product_description, box_type, dim_mm, material, gsm_spec, area_m2, qty, unit_price, line_total FROM quotation_lines WHERE quotation_id = ? AND COALESCE(deleted_at,'')='' ORDER BY line_no`, [quotationId]);
    } catch { /* 表不存在 (老 DB 兜底) */ }

    const soCode = `SO-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}-Q${q.id}`;
    const db = getDb();
    db.exec('BEGIN TRANSACTION');
    try {
      // v358.2 · 主 INSERT 不含 urgency 列 (老 schema 兼容) · 用单独 UPDATE 兜底
      db.run(
        `INSERT INTO sales_orders (code, customer_id, total_amount, status, created_at)
         VALUES (?, ?, ?, 'confirmed', datetime('now'))`,
        [soCode, q.customer_id, q.total]
      );
      const newSoId = query<{ id: number }>(`SELECT last_insert_rowid() AS id`)[0]?.id || 0;
      // 单独 UPDATE urgency (列存在生效, 不存在静默跳过)
      if (qUrgency === 'urgent' && newSoId) {
        try { db.run(`UPDATE sales_orders SET urgency = ? WHERE id = ?`, [qUrgency, newSoId]); } catch { /* col missing */ }
        // notes 加 🔥 兜底 (urgency 列缺时仍能识别)
        try { db.run(`UPDATE sales_orders SET notes = COALESCE(notes,'') || ' 🔥' WHERE id = ?`, [newSoId]); } catch { /* col missing */ }
      }
      // v358.1 · 把报价行项 → SO 行项 (套盒 N→N 复制)
      if (qLines.length > 0 && newSoId) {
        for (const ln of qLines) {
          try {
            db.run(
              `INSERT INTO sales_order_lines (sales_order_id, line_no, product_id, product_description, box_type, dim_mm, material, gsm_spec, area_m2, qty, unit_price, line_total, created_at)
               VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'))`,
              [newSoId, ln.line_no, ln.product_id, ln.product_description, ln.box_type, ln.dim_mm, ln.material, ln.gsm_spec, ln.area_m2, ln.qty, ln.unit_price, ln.line_total || (ln.qty * ln.unit_price)]
            );
          } catch { /* 表不存在 · 单行兜底 */ }
        }
      }
      db.run(`UPDATE quotations SET status = 'confirmed' WHERE id = ?`, [quotationId]);
      db.exec('COMMIT');
      await saveDb();

      const newSo = query<{ id: number }>(`SELECT id FROM sales_orders WHERE code = ?`, [soCode])[0];
      const lineCount = qLines.length;
      // Hand-off · 通知工程 + 采购 准备 BOM/PR
      sendNotification(['engineer', 'purchasing'], {
        title: `🆕 新销售订单 ${soCode}`,
        body: `报价 ${q.code} 已转 SO · 金额 đ${(q.total / 1e6).toFixed(1)}M · ${lineCount > 1 ? `${lineCount} 行套盒 · ` : ''}请准备 BOM + 物料`,
        severity: 'info',
        refType: 'sales_order',
        refId: newSo?.id,
        refHash: `salesOrderList`,
      });
      return {
        ok: true,
        message: `✅ 报价 ${q.code} 已转销售订单 ${soCode}${lineCount > 1 ? ` (${lineCount} 行套盒)` : ''}`,
        newRecord: { type: 'sales_order', id: newSo?.id || 0, code: soCode },
      };
    } catch (err) {
      db.exec('ROLLBACK');
      throw err;
    }
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * 转 SO 为 WO · 订单流第 4 节点 ⭐ Sprint 3 新增
 * 1. 查 SO 详情
 * 2. INSERT work_orders (status=planned · planned_qty=指定数量 · 关联 product_id)
 * 3. UPDATE sales_orders.status = 'in_production' (若仍 confirmed)
 * 4. saveDb
 */
export async function convertSOToWO(
  soId: number,
  opts: { productId: number; qty: number; dueDate?: string; supervisorId?: number; urgency?: string }
): Promise<WorkflowResult> {
  try {
    // v358.1 · 老 schema 兼容 · 先 query 基础列, urgency 单独查 (容缺列)
    const soBase = query<{ id: number; code: string; status: string; due_date: string | null }>(
      `SELECT id, code, status, due_date FROM sales_orders WHERE id = ?`, [soId]
    )[0];
    if (!soBase) return { ok: false, message: '❌ SO 不存在' };
    // urgency 单独 query · 列不存在则空 → 默认 normal
    const urgencyRow = query<{ urgency: string | null }>(`SELECT urgency FROM sales_orders WHERE id = ?`, [soId])[0];
    const so = { ...soBase, urgency: urgencyRow?.urgency || 'normal' };

    // v358.1 · 套盒多行支持: 优先按 sales_order_lines 循环生成 N WO · 单行兜底
    interface SOLine {
      id: number; line_no: number; product_id: number | null; product_description: string | null;
      box_type: string | null; dim_mm: string | null; qty: number; unit_price: number;
    }
    let soLines: SOLine[] = [];
    try {
      soLines = query<SOLine>(`SELECT id, line_no, product_id, product_description, box_type, dim_mm, qty, unit_price FROM sales_order_lines WHERE sales_order_id = ? AND COALESCE(deleted_at,'')='' AND COALESCE(wo_id,0)=0 ORDER BY line_no`, [soId]);
    } catch { /* 表不存在兜底 */ }

    // 套盒 N 行 → N WO
    const createdWos: { id: number; code: string; line_no: number; product_desc: string }[] = [];
    const db = getDb();
    const inheritedUrgency = opts.urgency || so.urgency || 'normal';
    const inheritedDueDate = opts.dueDate || so.due_date || null;

    db.exec('BEGIN TRANSACTION');
    try {
      if (soLines.length > 0) {
        // 多行套盒模式 · woCode 用 line_no 保唯一 (避免 sleep 打断 transaction)
        const baseStamp = String(Date.now()).slice(-4);
        for (const ln of soLines) {
          const woCode = `WO-${new Date().getFullYear()}-${baseStamp}-SO${so.id}-L${ln.line_no}`;
          db.run(
            `INSERT INTO work_orders (code, so_id, product_id, planned_qty, completed_qty, status, urgency, due_date, responsible_supervisor_id, created_at)
             VALUES (?, ?, ?, ?, 0, 'planned', ?, ?, ?, datetime('now'))`,
            [woCode, so.id, ln.product_id || opts.productId, ln.qty, inheritedUrgency, inheritedDueDate, opts.supervisorId || null]
          );
          const woId = query<{ id: number }>(`SELECT last_insert_rowid() AS id`)[0]?.id || 0;
          // 反向回填 sales_order_lines.wo_id
          try { db.run(`UPDATE sales_order_lines SET wo_id = ? WHERE id = ?`, [woId, ln.id]); } catch { /* col missing */ }
          createdWos.push({ id: woId, code: woCode, line_no: ln.line_no, product_desc: ln.product_description || `行 ${ln.line_no}` });
        }
      } else {
        // 单行兼容老 SO
        const woCode = `WO-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}-SO${so.id}`;
        db.run(
          `INSERT INTO work_orders (code, so_id, product_id, planned_qty, completed_qty, status, urgency, due_date, responsible_supervisor_id, created_at)
           VALUES (?, ?, ?, ?, 0, 'planned', ?, ?, ?, datetime('now'))`,
          [woCode, so.id, opts.productId, opts.qty, inheritedUrgency, inheritedDueDate, opts.supervisorId || null]
        );
        const woId = query<{ id: number }>(`SELECT last_insert_rowid() AS id`)[0]?.id || 0;
        createdWos.push({ id: woId, code: woCode, line_no: 1, product_desc: '主产品' });
      }

      if (so.status === 'confirmed') {
        db.run(`UPDATE sales_orders SET status = 'in_production' WHERE id = ?`, [soId]);
      }
      db.exec('COMMIT');
      await saveDb();
    } catch (err) {
      db.exec('ROLLBACK');
      throw err;
    }

    const isKit = createdWos.length > 1;
    const woListMsg = isKit ? `${createdWos.length} 工单 (套盒): ${createdWos.map(w => w.code).join(', ')}` : createdWos[0].code;
    // Hand-off · 通知班长 + 工人 准备开工 (每个 WO 单独通知 · 简化为一次汇总)
    sendNotification(['supervisor', 'worker'], {
      title: `🏭 新工单 · SO ${so.code} 派工`,
      body: `${woListMsg} · 交期 ${inheritedDueDate || '-'} · ${inheritedUrgency === 'urgent' ? '🔥 加急!' : ''}`,
      severity: inheritedUrgency === 'urgent' ? 'warning' : 'info',
      refType: 'work_order',
      refId: createdWos[0].id,
      refHash: `woManage`,
    });
    return {
      ok: true,
      message: `✅ SO ${so.code} 已派 ${createdWos.length} 工单${isKit ? ` (套盒 ${createdWos.length} 组件)` : ''}`,
      newRecord: { type: 'work_order', id: createdWos[0].id, code: createdWos[0].code },
    };
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * 转 PR 为 PO · 采购流核心
 */
export async function convertPRToPO(prId: number, supplierId: number): Promise<WorkflowResult> {
  try {
    const pr = query<{ id: number; code: string }>(
      `SELECT id, code FROM purchase_requisitions WHERE id = ?`, [prId]
    )[0];
    if (!pr) return { ok: false, message: '❌ PR 不存在' };

    const poCode = `PO-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-PR${pr.id}`;
    const db = getDb();
    db.run(
      `INSERT INTO purchase_orders (code, supplier_id, total_amount, status, created_at)
       VALUES (?, ?, 0, 'approved', datetime('now'))`,
      [poCode, supplierId]
    );
    await saveDb();

    // Hand-off · 通知采购 + 原料仓 准备收料
    sendNotification(['purchasing', 'warehouse'], {
      title: `🛒 新采购订单 ${poCode}`,
      body: `PR ${pr.code} 已转 PO · 等待供应商发货 · 准备 IQC + 入库`,
      severity: 'info',
      refType: 'purchase_order',
      refHash: `poList`,
    });
    return { ok: true, message: `✅ PR ${pr.code} 已转 PO ${poCode}`, newRecord: { type: 'po', id: 0, code: poCode } };
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * 工单完工 · 生产→仓储联动
 * 1. UPDATE work_orders.status = 'completed' + end_date
 * 2. INSERT inventory (成品入库)
 * 3. 通知 OQC 出货检验
 */
export async function completeWO(woId: number, qty: number, opts?: { reason?: string }): Promise<WorkflowResult> {
  try {
    const wo = query<any>(
      `SELECT id, code, product_id, status, planned_qty, so_id FROM work_orders WHERE id = ?`, [woId]
    )[0];
    if (!wo) return { ok: false, message: '❌ 工单不存在' };
    if (['completed', 'done', 'finished'].includes(wo.status)) {
      return { ok: false, message: '⚠️ 该工单已完工' };
    }

    const planned = wo.planned_qty || 0;
    const shortage = Math.max(0, planned - qty);
    const shortagePct = planned > 0 ? (shortage / planned) * 100 : 0;

    const db = getDb();
    db.exec('BEGIN TRANSACTION');
    try {
      db.run(
        `UPDATE work_orders SET status = 'completed', completed_qty = ?, end_date = datetime('now') WHERE id = ?`,
        [qty, woId]
      );
      db.run(`INSERT INTO inventory (product_id, qty, created_at) VALUES (?, ?, datetime('now'))`,
        [wo.product_id, qty]);

      // v364 P0 #11 · 自动检测缺数 (> 1% 即记录 shortage_log)
      let shortageId: number | null = null;
      if (shortage > 0 && shortagePct >= 1) {
        const code = `SH-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-WO${woId}`;
        // 取 customer_id 从 SO (若 wo.so_id 不空)
        const cust = wo.so_id ? query<any>(`SELECT customer_id FROM sales_orders WHERE id=?`, [wo.so_id])[0] : null;
        db.run(
          `INSERT INTO shortage_logs (code, wo_id, wo_code, so_id, customer_id, product_id, planned_qty, actual_qty, shortage_qty, shortage_pct, reason_category, reason_detail, status, reported_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'open', datetime('now'))`,
          [code, woId, wo.code, wo.so_id, cust?.customer_id || null, wo.product_id, planned, qty, shortage, Math.round(shortagePct * 100) / 100, 'production', opts?.reason || '生产损耗 / Hao hụt SX']
        );
        shortageId = query<any>(`SELECT last_insert_rowid() AS id`)[0]?.id || null;
      }

      db.exec('COMMIT');
      await saveDb();
      // Hand-off · 通知品控 OQC + 成品仓 (含缺数信息)
      const shortageNote = shortage > 0 ? ` · ⚠️ 缺数 ${shortage} 件 (${shortagePct.toFixed(1)}%)` : '';
      sendNotification(['quality', 'warehouse'], {
        title: `${shortage > 0 ? '⚠️' : '✅'} 工单完工 ${wo.code}${shortageNote}`,
        body: `${qty} 件已入库 (计划 ${planned}) · 等待 OQC 出厂检${shortage > 0 ? ` · 缺 ${shortage} 件待处理 (#shortageList)` : ''}`,
        severity: shortage > 0 ? 'warning' : 'info',
        refType: 'work_order',
        refId: woId,
        refHash: shortage > 0 ? 'shortageList' : 'woManage',
      });
      // 若有缺数 · 同时通知销售 + 客户经理 (他们要决定补数还是部分发货)
      if (shortage > 0) {
        sendNotification(['sales', 'admin', 'gm'], {
          title: `🔥 缺数告警 · ${wo.code}`,
          body: `计划 ${planned} 件 · 实际 ${qty} 件 · 缺 ${shortage} 件 (${shortagePct.toFixed(1)}%) · 请决定: 补数 / 部分发货 / 与客户协商`,
          severity: 'warning',
          refType: 'shortage',
          refId: shortageId || undefined,
          refHash: 'shortageList',
        });
      }
      return {
        ok: true,
        message: shortage > 0
          ? `⚠️ 工单 ${wo.code} 已完工 · 实际 ${qty} 件 (缺 ${shortage} · ${shortagePct.toFixed(1)}%) · 已自动记录 shortage_log${shortageId ? ' #' + shortageId : ''}`
          : `✅ 工单 ${wo.code} 已完工 · ${qty} 件已入库 · 数量足额`,
      };
    } catch (err) {
      db.exec('ROLLBACK');
      throw err;
    }
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * v364 Sprint I-C · 创建补数工单 (rework)
 *
 * 由 缺数 (shortageId) 或 退货 (returnId) 触发 · 自动生成新 WO 入排程
 * - 复制原 WO 的 product_id + so_id
 * - 数量 = qty_to_remake
 * - 标 reason + status='planned'
 * - 关联 shortage_logs.rework_wo_id (反向追溯)
 */
export async function createReworkWO(opts: {
  parentWoId?: number;
  shortageId?: number;
  returnId?: number;
  qty: number;
  reason: string;
  reasonCategory?: 'shortage' | 'customer_return' | 'quality_reject' | 'wrong_spec';
  priority?: 'urgent' | 'high' | 'medium' | 'low';
}): Promise<WorkflowResult> {
  try {
    if (opts.qty <= 0) return { ok: false, message: '❌ 补数数量必须 > 0' };

    // 反查 parent WO 信息
    let parentWo: any = null;
    if (opts.parentWoId) {
      parentWo = query<any>(`SELECT id, code, so_id, product_id, customer_id FROM work_orders WHERE id=?`, [opts.parentWoId])[0];
    } else if (opts.shortageId) {
      const sh = query<any>(`SELECT wo_id FROM shortage_logs WHERE id=?`, [opts.shortageId])[0];
      if (sh?.wo_id) parentWo = query<any>(`SELECT id, code, so_id, product_id FROM work_orders WHERE id=?`, [sh.wo_id])[0];
    } else if (opts.returnId) {
      // returns 表关联 SO · 反查 WO
      const ret = query<any>(`SELECT so_id, customer_id, product_id FROM returns WHERE id=?`, [opts.returnId])[0];
      if (ret?.so_id) {
        parentWo = query<any>(`SELECT id, code, so_id, product_id FROM work_orders WHERE so_id=? LIMIT 1`, [ret.so_id])[0];
      }
    }
    if (!parentWo) return { ok: false, message: '❌ 无法找到关联工单 / SO' };

    const db = getDb();
    const now = new Date();
    const ymd = now.toISOString().slice(0, 10).replace(/-/g, '');
    const reworkCode = `RWO-${ymd}-${Math.floor(Math.random() * 9000 + 1000)}`;
    const newWoCode = `${parentWo.code}-RWK${Math.floor(Math.random() * 99 + 1)}`;
    const customerId = parentWo.customer_id || (parentWo.so_id ? query<any>(`SELECT customer_id FROM sales_orders WHERE id=?`, [parentWo.so_id])[0]?.customer_id : null);

    db.exec('BEGIN TRANSACTION');
    try {
      // 1. INSERT rework_orders (主单)
      db.run(
        `INSERT INTO rework_orders (code, parent_wo_id, parent_wo_code, shortage_id, return_id, so_id, customer_id, product_id, qty_to_remake, reason_category, reason_detail, priority, status, planned_completion_date, created_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'planned', date('now', '+7 days'), datetime('now'))`,
        [reworkCode, parentWo.id, parentWo.code, opts.shortageId || null, opts.returnId || null, parentWo.so_id || null, customerId, parentWo.product_id, opts.qty, opts.reasonCategory || 'shortage', opts.reason, opts.priority || 'high']
      );
      const reworkId = query<any>(`SELECT last_insert_rowid() AS id`)[0]?.id;

      // 2. INSERT 真新 work_orders (排程用)
      db.run(
        `INSERT INTO work_orders (code, so_id, product_id, planned_qty, completed_qty, status, urgency, due_date, end_date, created_at)
         VALUES (?, ?, ?, ?, 0, 'planned', ?, date('now', '+7 days'), NULL, datetime('now'))`,
        [newWoCode, parentWo.so_id || null, parentWo.product_id, opts.qty, opts.priority === 'urgent' ? 'urgent' : 'high']
      );
      const newWoId = query<any>(`SELECT last_insert_rowid() AS id`)[0]?.id;

      // 3. 回填 rework_orders.new_wo_id (Tracking)
      db.run(`UPDATE rework_orders SET new_wo_id=? WHERE id=?`, [newWoId, reworkId]);

      // 4. 标 shortage_log 为 in_progress (若来自缺数)
      if (opts.shortageId) {
        db.run(`UPDATE shortage_logs SET status='in_progress', decision='rework', rework_wo_id=?, resolved_at=datetime('now') WHERE id=?`, [newWoId, opts.shortageId]);
      }
      // 5. 标 returns 为 rework (若来自退货)
      if (opts.returnId) {
        try { db.run(`UPDATE returns SET decision='rework', rework_wo_id=?, processed_at=datetime('now') WHERE id=?`, [newWoId, opts.returnId]); } catch { /* col may not exist */ }
      }

      db.exec('COMMIT');
      await saveDb();

      // Hand-off · 通知 PMC + 车间主任 + 工艺 (新 WO 入排程)
      sendNotification(['supervisor', 'engineer', 'admin'], {
        title: `🔁 补数工单 ${newWoCode}`,
        body: `${opts.qty} 件 · 原因 ${opts.reason} · 关联 ${parentWo.code} · 优先 ${opts.priority || 'high'} · 7 天交期 · 请安排排产`,
        severity: 'warning',
        refType: 'work_order',
        refId: newWoId,
        refHash: 'woManage',
      });
      return { ok: true, message: `✅ 补数工单 ${reworkCode} 创建 · 新 WO ${newWoCode} (${opts.qty} 件) 已入排程` };
    } catch (err) {
      db.exec('ROLLBACK');
      throw err;
    }
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * v364 Sprint I-C · 客户退货处理
 *
 * 输入: 退货记录 ID + 处理决定 (rework/refund/credit_note/scrap/concession)
 * 输出: 更新 returns + 视决定触发 createReworkWO 或 INSERT credit_note
 */
export async function processCustomerReturn(returnId: number, decision: 'rework' | 'refund' | 'credit_note' | 'scrap' | 'concession', opts?: { reworkQty?: number; refundAmount?: number; notes?: string }): Promise<WorkflowResult> {
  try {
    const ret = query<any>(`SELECT id, so_id, customer_id, product_id, qty, reason FROM returns WHERE id=?`, [returnId])[0];
    if (!ret) return { ok: false, message: '❌ 退货记录不存在' };

    const db = getDb();
    db.exec('BEGIN TRANSACTION');
    try {
      try {
        db.run(
          `UPDATE returns SET decision=?, refund_amount=?, processed_at=datetime('now'), notes=COALESCE(notes,'') || ' · ' || ? WHERE id=?`,
          [decision, opts?.refundAmount || 0, opts?.notes || '', returnId]
        );
      } catch { /* legacy schema · column may be missing */ }
      db.exec('COMMIT');
      await saveDb();

      // 视决定触发后续
      if (decision === 'rework') {
        const reworkResult = await createReworkWO({
          returnId,
          qty: opts?.reworkQty || ret.qty || 0,
          reason: `客户退货补数 · ${ret.reason || '质量不合格'}`,
          reasonCategory: 'customer_return',
          priority: 'urgent', // 客户退货优先级最高
        });
        return reworkResult;
      }

      // 财务: 退款/credit note 通知财务处理
      if (decision === 'refund' || decision === 'credit_note') {
        sendNotification(['finance', 'sales'], {
          title: `💸 ${decision === 'refund' ? '退款' : 'Credit Note'} #${returnId}`,
          body: `客户退货已确认 ${decision} · 金额 đ${((opts?.refundAmount || 0) / 1e6).toFixed(1)}M · 财务请凭此 + 原始退货单处理`,
          severity: 'warning',
          refType: 'return',
          refId: returnId,
          refHash: 'returnsList',
        });
      }
      return { ok: true, message: `✅ 退货 #${returnId} 已处理 · 决定: ${decision}` };
    } catch (err) {
      db.exec('ROLLBACK');
      throw err;
    }
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * v365 Sprint I-D · 订单成本 + 利润核算
 *
 * 实时累加 (status=open) · 月结锁定 (status=closed)
 *
 * 成本拆分:
 *   - material_cost  = SUM (material_issues 关联此 SO 工单 · qty × unit_price)
 *                      若无 material_issues 表则 fallback SUM (BOM × wo.completed_qty)
 *   - labor_cost     = SUM (work_reports · duration_min × hourly_rate / 60)
 *                      hourly_rate 默 50000 VND/h (Trungnam SEED · 后期 employee.hourly_rate)
 *   - overhead_cost  = monthly_overhead 按工时占比分摊 (粗略 · 后期细化)
 *   - waste_cost     = shortage_qty × unit_price (缺数损失)
 *
 * 收入 = sales_order_lines SUM (qty × unit_price) · fallback sales_orders.total_amount
 */
export async function calcOrderCost(soId: number): Promise<WorkflowResult & { pnl?: any }> {
  try {
    const so = query<any>(`SELECT id, code, customer_id, total_amount FROM sales_orders WHERE id=?`, [soId])[0];
    if (!so) return { ok: false, message: '❌ SO 不存在' };

    // 1. 关联此 SO 的所有 WO
    const wos = query<any>(`SELECT id, code, product_id, planned_qty, completed_qty FROM work_orders WHERE so_id=? AND COALESCE(deleted_at,'')=''`, [soId]);
    const woIds = wos.map(w => w.id);
    if (woIds.length === 0) return { ok: false, message: '⚠️ 此 SO 无关联 WO · 无法计算成本' };
    const plannedTotal = wos.reduce((s, w) => s + (w.planned_qty || 0), 0);
    const completedTotal = wos.reduce((s, w) => s + (w.completed_qty || 0), 0);
    const shortageTotal = Math.max(0, plannedTotal - completedTotal);

    // 2. 收入: sales_order_lines · fallback so.total_amount
    let revenue = 0;
    try {
      const lines = query<any>(`SELECT SUM(COALESCE(line_total, qty * unit_price)) AS s FROM sales_order_lines WHERE sales_order_id=? AND COALESCE(deleted_at,'')=''`, [soId]);
      revenue = lines[0]?.s || 0;
    } catch { /* no lines */ }
    if (!revenue) revenue = so.total_amount || 0;

    // 3. 原料成本 · 从 material_issues 累加 (若有), 否则 fallback BOM × completed_qty
    let materialCost = 0;
    try {
      const woList = woIds.join(',');
      if (woList) {
        const matSum = query<any>(`SELECT COALESCE(SUM(qty * unit_price), 0) AS s FROM material_issues WHERE wo_id IN (${woList}) AND COALESCE(deleted_at,'')=''`)[0];
        materialCost = matSum?.s || 0;
      }
    } catch { /* material_issues 表不存在 */ }
    // Fallback · BOM 估算 (各产品 BOM × completed)
    if (materialCost === 0 && completedTotal > 0) {
      try {
        // 粗略估算: 每件成品 50000 VND 原料 (Trungnam 平均) · 真实环境从 BOM 反查
        materialCost = completedTotal * 50000;
      } catch { /* ignore */ }
    }

    // 4. 工时成本 · 累加 work_reports
    let laborCost = 0;
    try {
      const woList = woIds.join(',');
      if (woList) {
        const lab = query<any>(`SELECT COALESCE(SUM(duration_min), 0) AS m FROM work_reports WHERE work_order_id IN (${woList})`)[0];
        const totalMin = lab?.m || 0;
        const hourlyRate = 50000; // VND/h · TODO v366 employee.hourly_rate
        laborCost = (totalMin / 60) * hourlyRate;
      }
    } catch { /* ignore */ }

    // 5. 间接费分摊 · 月度间接费按工时占比 (粗略)
    let overheadCost = 0;
    try {
      const now = new Date();
      const yyyymm = now.toISOString().slice(0, 7);
      const monthlyOh = query<any>(`SELECT COALESCE(SUM(amount), 0) AS s FROM monthly_overhead WHERE strftime('%Y-%m', period_date)=? OR period=?`, [yyyymm, yyyymm])[0];
      const monthOh = monthlyOh?.s || 0;
      // 按工时占比 (本订单工时 / 月总工时)
      const woList = woIds.join(',');
      if (woList && monthOh > 0) {
        const totalMonthMin = query<any>(`SELECT COALESCE(SUM(duration_min),0) AS m FROM work_reports WHERE strftime('%Y-%m', report_time)=?`, [yyyymm])[0]?.m || 1;
        const thisOrderMin = query<any>(`SELECT COALESCE(SUM(duration_min),0) AS m FROM work_reports WHERE work_order_id IN (${woList})`)[0]?.m || 0;
        overheadCost = (thisOrderMin / totalMonthMin) * monthOh;
      }
    } catch { /* monthly_overhead 表不存在或老 schema */ }

    // 6. 损耗 (缺数 × 单价)
    const unitPrice = completedTotal > 0 ? revenue / (completedTotal + shortageTotal) : (so.total_amount || 0) / (plannedTotal || 1);
    const wasteCost = shortageTotal * unitPrice;
    const wastePct = plannedTotal > 0 ? (shortageTotal / plannedTotal) * 100 : 0;

    // 7. 汇总
    const totalCost = materialCost + laborCost + overheadCost;
    const grossProfit = revenue - totalCost;
    const grossMarginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0;

    // 8. UPSERT order_pnl (有就 update, 没有就 insert)
    const db = getDb();
    const existing = query<any>(`SELECT id, status FROM order_pnl WHERE sales_order_id=? AND COALESCE(deleted_at,'')=''`, [soId])[0];
    if (existing) {
      if (existing.status === 'closed') {
        // 已锁定 · 不再重算
        const pnl = query<any>(`SELECT * FROM order_pnl WHERE id=?`, [existing.id])[0];
        return { ok: true, message: `✅ 订单 ${so.code} P&L 已锁定 (closed)`, pnl };
      }
      db.run(
        `UPDATE order_pnl SET completed_qty=?, shortage_qty=?, revenue=?, material_cost=?, labor_cost=?, overhead_cost=?, total_cost=?, gross_profit=?, gross_margin_pct=?, waste_qty=?, waste_cost=?, waste_pct=?, recalculated_at=datetime('now'), updated_at=datetime('now') WHERE id=?`,
        [completedTotal, shortageTotal, revenue, materialCost, laborCost, overheadCost, totalCost, grossProfit, grossMarginPct, shortageTotal, wasteCost, wastePct, existing.id]
      );
    } else {
      const productId = wos[0]?.product_id;
      db.run(
        `INSERT INTO order_pnl (sales_order_id, so_code, customer_id, product_id, planned_qty, completed_qty, shortage_qty, revenue, material_cost, labor_cost, overhead_cost, total_cost, gross_profit, gross_margin_pct, waste_qty, waste_cost, waste_pct, status, recalculated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'open', datetime('now'))`,
        [soId, so.code, so.customer_id, productId, plannedTotal, completedTotal, shortageTotal, revenue, materialCost, laborCost, overheadCost, totalCost, grossProfit, grossMarginPct, shortageTotal, wasteCost, wastePct]
      );
    }
    await saveDb();
    const pnl = query<any>(`SELECT * FROM order_pnl WHERE sales_order_id=? AND COALESCE(deleted_at,'')=''`, [soId])[0];
    return {
      ok: true,
      message: `✅ ${so.code} P&L 重算 · 营收 đ${(revenue / 1e6).toFixed(1)}M · 成本 đ${(totalCost / 1e6).toFixed(1)}M · 毛利 ${grossMarginPct.toFixed(1)}%`,
      pnl,
    };
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * v365 · 锁定订单 P&L (财务月结后用 · 不再允许重算)
 */
export async function closeOrderPnl(soId: number, closedBy: string): Promise<WorkflowResult> {
  try {
    // 先重算一次保证最新
    const calc = await calcOrderCost(soId);
    if (!calc.ok) return calc;
    const db = getDb();
    db.run(`UPDATE order_pnl SET status='closed', closed_at=datetime('now'), closed_by=? WHERE sales_order_id=? AND COALESCE(deleted_at,'')=''`, [closedBy, soId]);
    await saveDb();
    return { ok: true, message: `🔒 订单 P&L 已锁定 · 不再允许重算 (财务可凭此入账)` };
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * 创建 AR 发票 · SO 发货后开票
 */
export async function createARInvoice(soId: number): Promise<WorkflowResult> {
  try {
    const so = query<{ id: number; code: string; customer_id: number; total_amount: number; status: string }>(
      `SELECT id, code, customer_id, total_amount, status FROM sales_orders WHERE id = ?`, [soId]
    )[0];
    if (!so) return { ok: false, message: '❌ SO 不存在' };

    const code = `INV-AR-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-SO${so.id}`;
    getDb().run(
      `INSERT INTO customer_invoices (code, customer_id, total, paid_amount, status, created_at)
       VALUES (?, ?, ?, 0, 'unpaid', datetime('now'))`,
      [code, so.customer_id, so.total_amount]
    );
    await saveDb();
    // Hand-off · 通知财务 + 销售 跟单收款
    sendNotification(['finance', 'sales'], {
      title: `📄 新 AR 发票 ${code}`,
      body: `SO ${so.code} 已开票 · 金额 đ${(so.total_amount / 1e6).toFixed(1)}M · 待客户付款`,
      severity: 'info',
      refType: 'ar_invoice',
      refHash: `arInvoiceList`,
    });
    return { ok: true, message: `✅ AR 发票 ${code} 已开 · 金额 đ${(so.total_amount / 1e6).toFixed(1)}M` };
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * IQC 来料检验 ⭐ Sprint 4
 * 1. INSERT qc_records (qc_type=iqc)
 * 2. UPDATE material_receipts.iqc_status
 */
export async function submitIQC(opts: {
  receiptId: number;
  materialId: number;
  result: '合格' | '不合格' | '让步接收';
  ectValue?: number;
  burstValue?: number;
  notes?: string;
}): Promise<WorkflowResult> {
  try {
    const db = getDb();
    db.run(
      `INSERT INTO qc_records (qc_type, receipt_id, material_id, result, ect_value, burst_value, remark, created_at)
       VALUES ('iqc', ?, ?, ?, ?, ?, ?, datetime('now'))`,
      [opts.receiptId, opts.materialId, opts.result, opts.ectValue || null, opts.burstValue || null, opts.notes || null]
    );
    db.run(`UPDATE material_receipts SET iqc_status = ? WHERE id = ?`,
      [opts.result === '合格' ? 'passed' : 'failed', opts.receiptId]);
    await saveDb();
    // Hand-off · 合格通知仓储入库, 不合格通知采购退货
    if (opts.result === '合格') {
      sendNotification(['warehouse'], {
        title: `✅ IQC 合格 · 收料单 #${opts.receiptId}`,
        body: '可入库 · 请确认仓位',
        severity: 'info',
        refType: 'material_receipt',
        refId: opts.receiptId,
      });
    } else {
      sendNotification(['purchasing', 'warehouse'], {
        title: `❌ IQC 不合格 · 收料单 #${opts.receiptId}`,
        body: opts.notes || '请联系供应商退货',
        severity: 'warning',
        refType: 'material_receipt',
        refId: opts.receiptId,
      });
    }
    return { ok: true, message: `✅ IQC 已提交 · ${opts.result}` };
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * IPQC 在制检验 ⭐ Sprint 4
 */
export async function submitIPQC(opts: {
  woId: number;
  processId?: number;
  result: '合格' | '不合格' | '让步接收';
  ectValue?: number;
  burstValue?: number;
  notes?: string;
}): Promise<WorkflowResult> {
  try {
    getDb().run(
      `INSERT INTO qc_records (qc_type, work_order_id, process_id, result, ect_value, burst_value, remark, created_at)
       VALUES ('ipqc', ?, ?, ?, ?, ?, ?, datetime('now'))`,
      [opts.woId, opts.processId || null, opts.result, opts.ectValue || null, opts.burstValue || null, opts.notes || null]
    );
    await saveDb();
    return { ok: true, message: `✅ IPQC 已提交 · ${opts.result}` };
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * OQC 出厂检验 ⭐ Sprint 4
 */
export async function submitOQC(opts: {
  shippingNoteId: number;
  productId?: number;
  result: '合格' | '不合格' | '让步接收';
  ectValue?: number;
  burstValue?: number;
  notes?: string;
}): Promise<WorkflowResult> {
  try {
    const db = getDb();
    db.run(
      `INSERT INTO qc_records (qc_type, shipping_note_id, product_id, result, ect_value, burst_value, remark, created_at)
       VALUES ('oqc', ?, ?, ?, ?, ?, ?, datetime('now'))`,
      [opts.shippingNoteId, opts.productId || null, opts.result, opts.ectValue || null, opts.burstValue || null, opts.notes || null]
    );
    db.run(`UPDATE shipping_notes SET oqc_status = ? WHERE id = ?`,
      [opts.result === '合格' ? 'passed' : 'failed', opts.shippingNoteId]);
    await saveDb();
    // Hand-off · 合格通知仓储发货, 不合格通知销售客户
    if (opts.result === '合格') {
      sendNotification(['warehouse', 'sales'], {
        title: `✅ OQC 合格 · 发货单 #${opts.shippingNoteId}`,
        body: '可出库发货',
        severity: 'info',
        refType: 'shipping_note',
        refId: opts.shippingNoteId,
      });
    } else {
      sendNotification(['sales', 'supervisor', 'quality'], {
        title: `❌ OQC 不合格 · 发货单 #${opts.shippingNoteId}`,
        body: opts.notes || '阻发货 · 需返工或让步处理',
        severity: 'critical',
        refType: 'shipping_note',
        refId: opts.shippingNoteId,
      });
    }
    return { ok: true, message: `✅ OQC 已提交 · ${opts.result}` };
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * 收料入原料仓 ⭐ Sprint 4
 * 1. INSERT material_receipts
 * 2. UPDATE raw_materials.stock_qty += qty
 */
export async function receiveRawMaterial(opts: {
  poId?: number;
  supplierId?: number;
  materialId: number;
  materialType: 'raw' | 'auxiliary';
  qtyPlanned: number;
  qtyReceived: number;
  iqcStatus?: string;
  notes?: string;
}): Promise<WorkflowResult> {
  try {
    const code = `MR-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(Date.now()).slice(-4)}`;
    const db = getDb();
    db.exec('BEGIN TRANSACTION');
    try {
      db.run(
        `INSERT INTO material_receipts (code, po_id, supplier_id, material_type, material_id, qty, qty_received, receipt_date, iqc_status, notes, created_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, datetime('now'), ?, ?, datetime('now'))`,
        [code, opts.poId || null, opts.supplierId || null, opts.materialType, opts.materialId,
         opts.qtyPlanned, opts.qtyReceived, opts.iqcStatus || 'pending', opts.notes || null]
      );
      // 累加原料库存
      const table = opts.materialType === 'raw' ? 'raw_materials' : 'auxiliary_materials';
      db.run(`UPDATE ${table} SET stock_qty = COALESCE(stock_qty, 0) + ? WHERE id = ?`,
        [opts.qtyReceived, opts.materialId]);
      db.exec('COMMIT');
      await saveDb();
      // Hand-off · 通知 IQC 检验
      sendNotification(['quality'], {
        title: `📥 新收料 ${code}`,
        body: `${opts.qtyReceived}/${opts.qtyPlanned} 已到 · 等待 IQC 来料检`,
        severity: opts.qtyReceived < opts.qtyPlanned ? 'warning' : 'info',
        refType: 'material_receipt',
        refHash: `iqcIncoming`,
      });
      return { ok: true, message: `✅ 收料 ${code} · ${opts.qtyReceived} 已入库`, newRecord: { type: 'material_receipt', id: 0, code } };
    } catch (err) {
      db.exec('ROLLBACK');
      throw err;
    }
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * 创建发货单 ⭐ Sprint 4
 * 1. INSERT shipping_notes
 * 2. UPDATE sales_orders.status = 'shipped'
 */
export async function createShippingNote(opts: {
  soId: number;
  customerId: number;
  vehicleId?: number;
  driverId?: number;
  shipDate: string;
  totalQty: number;
  totalValue?: number;
  notes?: string;
}): Promise<WorkflowResult> {
  try {
    const code = `DL-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-SO${opts.soId}`;
    const db = getDb();
    db.exec('BEGIN TRANSACTION');
    try {
      db.run(
        `INSERT INTO shipping_notes (code, so_id, customer_id, vehicle_id, driver_id, ship_date, status, total_qty, total_value, notes, created_at)
         VALUES (?, ?, ?, ?, ?, ?, 'planned', ?, ?, ?, datetime('now'))`,
        [code, opts.soId, opts.customerId, opts.vehicleId || null, opts.driverId || null,
         opts.shipDate, opts.totalQty, opts.totalValue || null, opts.notes || null]
      );
      db.run(`UPDATE sales_orders SET status = 'shipped' WHERE id = ?`, [opts.soId]);
      db.exec('COMMIT');
      await saveDb();
      // Hand-off · 通知销售 + 财务 + 客户
      sendNotification(['sales', 'finance'], {
        title: `🚚 发货 ${code}`,
        body: `${opts.totalQty} 件 · 司机配送中 · 预计送达 · 准备开 AR 发票`,
        severity: 'info',
        refType: 'shipping_note',
        refHash: `arInvoiceCreate`,
      });
      return { ok: true, message: `✅ 发货单 ${code} 已创建`, newRecord: { type: 'shipping_note', id: 0, code } };
    } catch (err) {
      db.exec('ROLLBACK');
      throw err;
    }
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * 收款时联动更新 AR 发票 paid_amount
 * customerPaymentCreate 调用后, 自动找最旧未付 AR 发票分配
 */
export async function applyPaymentToARInvoices(customerId: number, amount: number): Promise<{ applied: number; remaining: number }> {
  let remaining = amount;
  let applied = 0;
  const invoices = query<{ id: number; total: number; paid_amount: number }>(
    `SELECT id, total, COALESCE(paid_amount, 0) AS paid_amount FROM customer_invoices
     WHERE customer_id = ? AND COALESCE(status, '') IN ('unpaid', 'partial')
     ORDER BY created_at ASC`,
    [customerId]
  );
  const db = getDb();
  for (const inv of invoices) {
    if (remaining <= 0) break;
    const unpaid = inv.total - inv.paid_amount;
    if (unpaid <= 0) continue;
    const pay = Math.min(remaining, unpaid);
    const newPaid = inv.paid_amount + pay;
    const newStatus = newPaid >= inv.total ? 'paid' : 'partial';
    db.run(`UPDATE customer_invoices SET paid_amount = ?, status = ? WHERE id = ?`, [newPaid, newStatus, inv.id]);
    remaining -= pay;
    applied += pay;
  }
  await saveDb();
  // Hand-off · 通知销售 + GM 收款到账 (仅有 applied 时)
  if (applied > 0) {
    sendNotification(['sales', 'gm', 'boss'], {
      title: `💰 收款 đ${(applied / 1e6).toFixed(1)}M`,
      body: `客户 #${customerId} 付款已 apply 到 ${applied >= amount ? '全部' : '部分'} AR 发票${remaining > 0 ? ` · 余款 đ${(remaining / 1e6).toFixed(1)}M 留账` : ''}`,
      severity: 'info',
      refType: 'customer_payment',
      refHash: `arAging`,
    });
  }
  return { applied, remaining };
}

// ============================================================
// v359 Sprint I-A · P0 #9 · 排单 4 件齐备 (原料/印版/刀模/油墨)
// ============================================================

export interface WOReadiness {
  wo_id: number;
  raw_material_status: string;
  plate_status: string;
  die_status: string;
  ink_status: string;
  raw_material_ref: string | null;
  plate_ref: string | null;
  die_ref: string | null;
  ink_ref: string | null;
  ready_at: string | null;
  all_ready: boolean;
}

/**
 * 检查 WO 4 件齐备状态 · 优先返已存 readiness 行, 否则现算
 * 4 件: raw_material (原料库存够) · plate (印版在库) · die (刀模在库) · ink (油墨库存够)
 */
export function checkWOReadiness(woId: number): WOReadiness {
  // 优先读已存 readiness
  let r = query<WOReadiness>(`SELECT wo_id, raw_material_status, plate_status, die_status, ink_status, raw_material_ref, plate_ref, die_ref, ink_ref, ready_at FROM wo_readiness WHERE wo_id = ? ORDER BY id DESC LIMIT 1`, [woId])[0];
  if (r) {
    return { ...r, all_ready: [r.raw_material_status, r.plate_status, r.die_status, r.ink_status].every(s => s === 'ready') };
  }
  // 现算 (从相关表 query)
  const wo = query<{ id: number; product_id: number | null; planned_qty: number }>(`SELECT id, product_id, planned_qty FROM work_orders WHERE id = ?`, [woId])[0];
  if (!wo) {
    return { wo_id: woId, raw_material_status: 'waiting', plate_status: 'waiting', die_status: 'waiting', ink_status: 'waiting', raw_material_ref: null, plate_ref: null, die_ref: null, ink_ref: null, ready_at: null, all_ready: false };
  }
  // 印版 by product_id (如有)
  let plateStatus = 'waiting', plateRef: string | null = null;
  if (wo.product_id) {
    const plate = query<{ code: string; status: string }>(`SELECT code, status FROM printing_plates WHERE product_id = ? OR code LIKE ? LIMIT 1`, [wo.product_id, `%${wo.product_id}%`])[0];
    if (plate) { plateStatus = plate.status === 'in_stock' ? 'ready' : 'waiting'; plateRef = plate.code; }
  }
  // 刀模 (默认 ready · 真业务按 product/customer JOIN)
  const die = query<{ code: string; status: string }>(`SELECT code, status FROM dies ORDER BY id DESC LIMIT 1`)[0];
  const dieStatus = die ? (die.status === 'in_stock' ? 'ready' : 'waiting') : 'waiting';
  const dieRef = die?.code || null;
  // 油墨 (检测库存 ≥ reorder 即 ready, 否则 waiting)
  const inkLow = query<{ n: number }>(`SELECT COUNT(*) AS n FROM inks WHERE stock_kg < reorder_kg`)[0]?.n || 0;
  const inkStatus = inkLow > 0 ? 'waiting' : 'ready';
  // 原料默认 waiting (真业务 from raw_materials)
  const rawStatus = 'waiting';
  const allReady = [rawStatus, plateStatus, dieStatus, inkStatus].every(s => s === 'ready');
  // 存 wo_readiness 给后续查
  try {
    const db = getDb();
    db.run(`INSERT INTO wo_readiness (wo_id, raw_material_status, plate_status, die_status, ink_status, plate_ref, die_ref, ready_at, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, datetime('now'))`,
      [woId, rawStatus, plateStatus, dieStatus, inkStatus, plateRef, dieRef, allReady ? new Date().toISOString() : null]);
  } catch { /* dup ok */ }
  return { wo_id: woId, raw_material_status: rawStatus, plate_status: plateStatus, die_status: dieStatus, ink_status: inkStatus, raw_material_ref: null, plate_ref: plateRef, die_ref: dieRef, ink_ref: null, ready_at: allReady ? new Date().toISOString() : null, all_ready: allReady };
}

/**
 * 手动标 4 件某项 ready (PMC/工程主管操作)
 */
export async function markReadinessReady(woId: number, item: 'raw' | 'plate' | 'die' | 'ink', ref: string): Promise<WorkflowResult> {
  try {
    const colMap: Record<string, [string, string]> = {
      raw:   ['raw_material_status', 'raw_material_ref'],
      plate: ['plate_status', 'plate_ref'],
      die:   ['die_status', 'die_ref'],
      ink:   ['ink_status', 'ink_ref'],
    };
    const [statusCol, refCol] = colMap[item];
    const db = getDb();
    // upsert
    let existing = query<{ id: number }>(`SELECT id FROM wo_readiness WHERE wo_id = ?`, [woId])[0];
    if (!existing) {
      db.run(`INSERT INTO wo_readiness (wo_id, created_at) VALUES (?, datetime('now'))`, [woId]);
    }
    db.run(`UPDATE wo_readiness SET ${statusCol} = 'ready', ${refCol} = ?, updated_at = datetime('now') WHERE wo_id = ?`, [ref, woId]);
    // 若 4 件全 ready 设 ready_at
    const cur = query<WOReadiness>(`SELECT raw_material_status, plate_status, die_status, ink_status FROM wo_readiness WHERE wo_id = ?`, [woId])[0];
    if (cur && [cur.raw_material_status, cur.plate_status, cur.die_status, cur.ink_status].every(s => s === 'ready')) {
      db.run(`UPDATE wo_readiness SET ready_at = datetime('now') WHERE wo_id = ?`, [woId]);
      // Hand-off · 通知 PMC + 班长 可开工
      sendNotification(['supervisor', 'worker'], {
        title: `✅ WO #${woId} 4 件齐备`,
        body: `原料 / 印版 / 刀模 / 油墨 全部到位 · 可立即开工`,
        severity: 'info',
        refType: 'work_order',
        refId: woId,
        refHash: `woDetail?id=${woId}`,
      });
    }
    await saveDb();
    return { ok: true, message: `✅ ${item} 标 ready · ref=${ref}` };
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

// ============================================================
// v360 Sprint I-B · P0 #10 · 来料扫码 + QR 收货小票
// ============================================================

export interface ReceiptQR {
  receiptNo: string;
  qrCode: string;
  receiptId: number;
}

/**
 * 收料 + 生成 QR · 仓库扫码确认到货
 */
export async function receiveMaterialWithQR(opts: {
  material_type: 'raw' | 'auxiliary' | 'plate' | 'die' | 'ink' | 'other';
  material_id?: number | null;
  material_name: string;
  batch_no?: string;
  qty: number;
  unit?: string;
  unit_price?: number;
  supplier_id?: number | null;
  supplier_name?: string;
  supplier_phone?: string;
  supplier_address?: string;
  po_no?: string;
  wo_id?: number | null;
  wo_code?: string;
  so_id?: number | null;
  so_code?: string;
  customer_name?: string;
  purchaser_code?: string;
  arrival_doc_no?: string;
  package_count?: number;
  gross_weight_kg?: number;
  warehouse_location?: string;
  receiver_code: string;
  notes?: string;
}): Promise<WorkflowResult & { receipt?: ReceiptQR }> {
  try {
    const ts = new Date();
    const ymd = ts.toISOString().slice(0, 10).replace(/-/g, '');
    const tail = Math.floor(Math.random() * 9000 + 1000);
    const receiptNo = `RC-${ymd}-${tail}`;
    // QR payload: 含 receipt_no + 物料 + 数量 + 时间 (扫码后系统能定位记录)
    const qrCode = `TN-RECEIPT:${receiptNo}|${opts.material_type}|${opts.material_name}|${opts.qty}${opts.unit || '件'}|${opts.batch_no || '-'}|${ts.toISOString().slice(0, 16)}`;
    const batch = opts.batch_no || `BATCH-${ymd}-${tail}`;
    const db = getDb();
    db.run(
      `INSERT INTO material_receipts_qr (receipt_no, qr_code, material_type, material_id, material_name, batch_no, qty, unit, unit_price, supplier_id, supplier_name, po_no, warehouse_location, receiver_code, receive_time, qc_status, notes, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'), 'pending', ?, datetime('now'))`,
      [receiptNo, qrCode, opts.material_type, opts.material_id || null, opts.material_name, batch, opts.qty, opts.unit || '件', opts.unit_price || 0, opts.supplier_id || null, opts.supplier_name || '', opts.po_no || '', opts.warehouse_location || '', opts.receiver_code, opts.notes || '']
    );
    const id = query<{ id: number }>(`SELECT last_insert_rowid() AS id`)[0]?.id || 0;
    // v360.1 · 单独 UPDATE 扩展字段 (老 schema 缺列时静默跳过)
    if (id) {
      for (const [col, val] of [
        ['wo_id', opts.wo_id || null], ['wo_code', opts.wo_code || ''],
        ['so_id', opts.so_id || null], ['so_code', opts.so_code || ''],
        ['customer_name', opts.customer_name || ''], ['purchaser_code', opts.purchaser_code || ''],
        ['arrival_doc_no', opts.arrival_doc_no || ''], ['package_count', opts.package_count || null],
        ['gross_weight_kg', opts.gross_weight_kg || null],
      ] as const) {
        try { db.run(`UPDATE material_receipts_qr SET ${col} = ? WHERE id = ?`, [val, id]); } catch { /* col missing */ }
      }
    }
    await saveDb();
    // Hand-off · 通知 IQC 待检
    sendNotification(['quality', 'purchasing'], {
      title: `📦 新到料 ${receiptNo}`,
      body: `${opts.material_name} · ${opts.qty} ${opts.unit || '件'} · 批号 ${batch} · 供应商 ${opts.supplier_name || '-'} · 待 IQC 检验`,
      severity: 'info',
      refType: 'material_receipt',
      refId: id,
      refHash: `iqcIncoming`,
    });
    return {
      ok: true,
      message: `✅ 收料 ${receiptNo} · QR 已生成 · 打印小票贴货物`,
      receipt: { receiptNo, qrCode, receiptId: id },
    };
  } catch (err) {
    return { ok: false, message: '❌ ' + (err instanceof Error ? err.message : String(err)) };
  }
}

/**
 * 扫码 QR 反查收货单 · 全生命周期追溯
 */
export function lookupReceiptByQR(qr: string): any | null {
  const r = query<any>(`SELECT * FROM material_receipts_qr WHERE qr_code = ? OR receipt_no = ?`, [qr, qr])[0];
  return r || null;
}
